import ArrowDownTrayIcon from '@heroicons/react/24/outline/ArrowDownTrayIcon';
import ArrowLeftIcon from '@heroicons/react/24/outline/ArrowLeftIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { useTranslation } from '@/hooks/useTranslation';
import type { DamStatusBadge } from '@/pages/dam/utils/damStatusBadges';

import { StatusBadge } from '../DamAssetCard/DamCardBadgeRow';

interface DamAssetDetailHeaderProps {
    title: string;
    /** Badges of the `HEADER_BADGES` section of `asset_detail`. */
    badges: DamStatusBadge[];
    onBack: () => void;
    onDownload: () => void;
    onDelete: () => void;
}

/**
 * Page header of the asset detail (wireframe `6989:65387`): back, file name, status badges,
 * Download and Delete. Below `xl` the actions are icon-only and the badges move below the
 * title (md / sm breakpoints).
 */
function DamAssetDetailHeader({ title, badges, onBack, onDownload, onDelete }: DamAssetDetailHeaderProps) {
    const { t } = useTranslation();
    const badgeList = badges.map((badge, index) => <StatusBadge key={`${badge.label}-${index}`} badge={badge} />);

    return (
        <header className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-py-4 md:tw-px-8 xl:tw-px-10">
            <div className="tw-flex tw-items-center tw-gap-2">
                <ButtonPanel isDisabled={false} icon={ArrowLeftIcon} tooltip={t('DAM.DETAIL.BACK')} aria-label={t('DAM.DETAIL.BACK')} onClick={onBack} />
                <h1 className="tw-m-0 tw-min-w-0 tw-truncate tw-text-t-lg tw-font-bold tw-text-h-primary lg:tw-text-t-xl" title={title}>
                    {title}
                </h1>
                {badgeList.length > 0 && <div className="tw-hidden tw-shrink-0 tw-gap-2 xl:tw-flex">{badgeList}</div>}
                <div className="tw-ml-auto tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <Button hasIcon icon={ArrowDownTrayIcon} onClick={onDownload} className="tw-hidden xl:tw-flex">
                        {t('DAM.DETAIL.DOWNLOAD')}
                    </Button>
                    <Button variant="outline" hasIcon icon={TrashIcon} onClick={onDelete} className="tw-hidden xl:tw-flex">
                        {t('DAM.DETAIL.DELETE')}
                    </Button>
                    <ButtonPanel
                        isDisabled={false}
                        icon={ArrowDownTrayIcon}
                        tooltip={t('DAM.DETAIL.DOWNLOAD')}
                        aria-label={t('DAM.DETAIL.DOWNLOAD')}
                        onClick={onDownload}
                        className="xl:tw-hidden"
                    />
                    <ButtonPanel
                        isDisabled={false}
                        icon={TrashIcon}
                        variant="destructive"
                        tooltip={t('DAM.DETAIL.DELETE')}
                        aria-label={t('DAM.DETAIL.DELETE')}
                        onClick={onDelete}
                        className="xl:tw-hidden"
                    />
                </div>
            </div>
            {badgeList.length > 0 && <div className="tw-flex tw-flex-wrap tw-gap-2 xl:tw-hidden">{badgeList}</div>}
        </header>
    );
}

export { DamAssetDetailHeader };
export type { DamAssetDetailHeaderProps };
